'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause } from 'lucide-react';
import { API } from '@/lib/utils/api';

interface TTSPlayerProps {
  text: string;
  language?: 'en' | 'he';
  isFirst?: boolean;
  autoPlay?: boolean;
  onPlay?: () => void;
}

export function TTSPlayer({ text, language = 'en', isFirst = false, autoPlay = false, onPlay }: TTSPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start as loading
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  const isHebrew = language === 'he';

  const labels = {
    en: { start: 'Start', play: 'Play', pause: 'Pause', loading: '...' },
    he: { start: 'התחל', play: 'נגן', pause: 'השהה', loading: '...' },
  };

  const t = labels[language];

  // Preload audio on mount
  useEffect(() => {
    loadAudio();
  }, [loadAudio]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  // Update time as audio plays
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isReady]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const loadAudio = useCallback(async () => {
    if (audioUrlRef.current) return; // Already loaded

    setIsLoading(true);
    try {
      const response = await fetch(API.tts, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: isHebrew ? 'nova' : 'alloy' }),
      });

      if (!response.ok) throw new Error('Failed to load audio');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;

      if (!audioRef.current) {
        audioRef.current = new Audio(url);
      } else {
        audioRef.current.src = url;
      }

      await audioRef.current.load();
      setDuration(audioRef.current.duration || 0);
      setIsReady(true);

      // If autoPlay was requested, start playing now
      if (autoPlay) {
        onPlay?.();
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error loading audio:', error);
    } finally {
      setIsLoading(false);
    }
  }, [text, isHebrew, autoPlay, onPlay]);

  const handlePlayPause = async () => {
    if (!isReady || !audioRef.current) return;

    // Notify parent on first play
    if (!isPlaying && currentTime === 0) {
      onPlay?.();
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      await audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const getButtonText = () => {
    if (isLoading) return t.loading;
    if (isPlaying) return t.pause;
    if (isFirst && currentTime === 0) return t.start;
    return t.play;
  };

  return (
    <div className="w-full flex items-center gap-3 p-3 bg-card border border-border rounded-xl" dir="ltr">
      {/* Play/Pause Button */}
      <button
        onClick={handlePlayPause}
        disabled={isLoading || !isReady}
        className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 shrink-0"
      >
        {isLoading ? (
          <span className="animate-pulse">{t.loading}</span>
        ) : (
          <>
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {getButtonText()}
          </>
        )}
      </button>

      {/* Track Bar */}
      <div className="flex-1 flex items-center">
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={currentTime}
          onChange={handleSeek}
          disabled={!isReady || duration === 0}
          className="flex-1 w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      {/* Time Display */}
      <div className="font-mono text-sm text-muted-foreground whitespace-nowrap shrink-0">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  );
}
